<script setup lang="ts">
import { diContainer } from '@/coreContainer/container';
import {
  cloneUnmanagedBusinessAutoRegisterCondition,
  cloneUnmanagedBusinessAutoRegisterSettingItem,
  createDefaultUnmanagedBusinessAutoRegisterCondition,
  createDefaultUnmanagedBusinessAutoRegisterOptionalRegisterItem,
  createDefaultUnmanagedBusinessAutoRegisterSettingItem,
  createUnmanagedBusinessAutoRegisterSettingId,
  getConditionSearchTypesByFieldType,
  normalizeUnmanagedBusinessAutoRegisterCondition,
  UnmanagedBusinessAutoRegisterCondition,
  UnmanagedBusinessAutoRegisterConditionFieldType,
  UnmanagedBusinessAutoRegisterField,
  UnmanagedBusinessAutoRegisterSettingConditionItem,
  UnmanagedBusinessAutoRegisterSettingItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import SuggestInput from '@/corePagesModule/components/SuggestInput.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import SelectInput from '@/corePagesModule/components/SelectInput.vue';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import {
  numericOptionalRegisterFields,
  optionalRegisterFields,
  requiredConditionFieldTypes,
} from '@/settingsModule/constants/unmanagedBusinessAutoRegister';
import { useUnmanagedBusinessAutoRegisterLabels } from '@/settingsModule/composables/useUnmanagedBusinessAutoRegisterLabels';
import { useUnmanagedBusinessAutoRegisterFormValidation } from '@/settingsModule/composables/useUnmanagedBusinessAutoRegisterFormValidation';
import { useUnmanagedBusinessAutoRegisterConditionOptions } from '@/settingsModule/composables/useUnmanagedBusinessAutoRegisterConditionOptions';
import { useUnmanagedBusinessAutoRegisterOptionalItemOptions } from '@/settingsModule/composables/useUnmanagedBusinessAutoRegisterOptionalItemOptions';
import { SettingPageMode, useSettingUnmanagedBusinessStore } from '@/settingsModule/pages/settingContent/useSettingUnmanagedBusinessStore';
import { onBeforeMount, reactive, ref } from 'vue';

const t = i18n.t;

/**
 * 条件候補追加フォームの入力値
 */
interface NewConditionOptionInput {
  targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType;
  code: string;
  name: string;
  value: string;
}

/**
 * サーバー取得値反映時の画面状態オプション
 */
interface ApplyServerSettingOptions {
  previousOpenedSettingIds?: string[];
  openFirstIfNone?: boolean;
}

const {
  getConditionFieldTypeLabel,
  getFieldLabel,
  getRegisterUnitLabel,
  getConditionFieldTypeOptions,
  getOptionalFieldOptions,
  getRegisterUnitOptions,
  convertLabelToConditionFieldType,
  convertLabelToConditionSearchType,
  convertLabelToTargetField,
  convertLabelToRegisterUnit,
  getSearchTypeLabelsByConditionItem,
  getConditionSearchTypeSelectLabel,
} = useUnmanagedBusinessAutoRegisterLabels();

const settingUnmanagedBusinessStore = useSettingUnmanagedBusinessStore();
const {
  state,
  resetState,
  setInitialized,
  setIsSaving,
  setMode,
  setEditingSettingId,
  setCondition,
  setUpdateAt,
  setOpenedSettingIds,
  toggleOpenedSetting,
  setFormSetting,
  setFormConditions,
  setFormSettingName,
  setFormLeadTime,
  setFormStdInventoryIndicesMin,
  setFormStdInventoryIndicesMax,
  setFormConditionSearchType,
  setFormConditionTargetValue,
  addFormOptionalItem,
  removeFormOptionalItem,
  setFormOptionalItemTargetField,
  setFormOptionalItemTargetValue,
  setFormLeadTimeUnit,
  setFormStdInventoryIndicesUnit,
} = settingUnmanagedBusinessStore;

/**
 * 検証対象のフォーム設定を取得する
 */
const getCurrentFormSetting = (): UnmanagedBusinessAutoRegisterSettingItem => {
  return state.formSetting;
};

const numericInputValues = reactive({
  leadTime: '',
  stdInventoryIndicesMin: '',
  stdInventoryIndicesMax: '',
});

/**
 * 登録内容の数値入力値を返す
 */
const getNumericInputValues = (): typeof numericInputValues => {
  return numericInputValues;
};

/**
 * 登録内容の数値入力欄を設定値で初期化する
 */
const syncNumericInputValues = (): void => {
  numericInputValues.leadTime = String(state.formSetting.registerContent.leadTime);
  numericInputValues.stdInventoryIndicesMin = String(state.formSetting.registerContent.stdInventoryIndicesMin);
  numericInputValues.stdInventoryIndicesMax = String(state.formSetting.registerContent.stdInventoryIndicesMax);
};

/**
 * 数値入力文字列を数値へ変換する
 * @param rawValue - 入力文字列
 * @param fallbackValue - 変換失敗時に使用する値
 */
const convertInputToNumber = (rawValue: string, fallbackValue: number): number => {
  const trimmedValue = rawValue.trim();
  if (trimmedValue === '') {
    return fallbackValue;
  }
  const parsedValue = Number(trimmedValue);
  if (Number.isFinite(parsedValue)) {
    return parsedValue;
  }
  return fallbackValue;
};

/**
 * 設定名入力イベントを処理する
 * @param event - inputイベント
 */
const onSettingNameInput = (event: Event): void => {
  const value = (event.target as HTMLInputElement).value;
  setFormSettingName(value);
  updateSettingNameValidation(value);
};

/**
 * リードタイム入力イベントを処理する
 * @param event - inputイベント
 */
const onLeadTimeInput = (event: Event): void => {
  const value = (event.target as HTMLInputElement).value;
  numericInputValues.leadTime = value;
  setFormLeadTime(convertInputToNumber(value, state.formSetting.registerContent.leadTime));
  updateLeadTimeValidation(value);
};

/**
 * 標準在庫指数min入力イベントを処理する
 * @param event - inputイベント
 */
const onStdInventoryIndicesMinInput = (event: Event): void => {
  const value = (event.target as HTMLInputElement).value;
  numericInputValues.stdInventoryIndicesMin = value;
  setFormStdInventoryIndicesMin(convertInputToNumber(value, state.formSetting.registerContent.stdInventoryIndicesMin));
  updateStdInventoryIndicesMinValidation(value);
};

/**
 * 標準在庫指数max入力イベントを処理する
 * @param event - inputイベント
 */
const onStdInventoryIndicesMaxInput = (event: Event): void => {
  const value = (event.target as HTMLInputElement).value;
  numericInputValues.stdInventoryIndicesMax = value;
  setFormStdInventoryIndicesMax(convertInputToNumber(value, state.formSetting.registerContent.stdInventoryIndicesMax));
  updateStdInventoryIndicesMaxValidation(value);
};

/**
 * 任意項目値の更新イベントを処理する
 * @param optionalItemIndex - 任意項目index
 * @param targetValue - 更新後の値
 */
const onOptionalItemTargetValueUpdate = (optionalItemIndex: number, targetValue: string): void => {
  setFormOptionalItemTargetValue(optionalItemIndex, targetValue);
  revalidateOptionalItemsIfNeeded();
};

/**
 * 任意項目で既に使用中の対象フィールド一覧を返す
 */
const getUsedOptionalFields = (): UnmanagedBusinessAutoRegisterField[] => {
  return state.formSetting.registerContent.optionalItems.map((optionalItem) => optionalItem.targetField);
};

/**
 * 任意項目indexごとに選択可能な対象フィールド一覧を返す
 * @param optionalItemIndex - 任意項目index
 */
const getSelectableOptionalFields = (optionalItemIndex: number): UnmanagedBusinessAutoRegisterField[] => {
  const usedFieldsByOthers = new Set(
    state.formSetting.registerContent.optionalItems
      .filter((_, index) => index !== optionalItemIndex)
      .map((optionalItem) => optionalItem.targetField)
  );
  const currentField = state.formSetting.registerContent.optionalItems[optionalItemIndex]?.targetField;

  return optionalRegisterFields.filter((optionalField) => {
    return optionalField === currentField || !usedFieldsByOthers.has(optionalField);
  });
};

/**
 * 任意項目選択肢ラベル一覧を返す
 * @param optionalItemIndex - 任意項目index
 */
const getOptionalFieldSelectOptions = (optionalItemIndex: number): string[] => {
  return getOptionalFieldOptions(getSelectableOptionalFields(optionalItemIndex));
};

/**
 * 任意項目を追加可能か判定する
 */
const canAddOptionalItem = (): boolean => {
  const usedFields = new Set(getUsedOptionalFields());
  return optionalRegisterFields.some((optionalField) => !usedFields.has(optionalField));
};

const {
  fieldState,
  syncFormValidationState,
  revalidateConditionValuesIfNeeded,
  revalidateOptionalItemsIfNeeded,
  updateSettingNameValidation,
  updateLeadTimeValidation,
  updateStdInventoryIndicesMinValidation,
  updateStdInventoryIndicesMaxValidation,
  validateForm,
  resetValidationState,
} = useUnmanagedBusinessAutoRegisterFormValidation({
  getFormSetting: getCurrentFormSetting,
  getNumericInputValues,
  requiredConditionFieldTypes,
  numericOptionalRegisterFields,
  getFieldLabel,
});

const {
  resetConditionValueOptions,
  appendConditionOptionValue,
  appendConditionOptionPair,
  loadConditionValueOptionsFromApi,
  appendConditionValueOptionsFromSettings,
  getConditionValueOptions,
  getConditionCounterpartText,
  getConditionCodeNamePair,
  resolveConditionCodeNamePair,
  syncConditionCounterpartOption,
} = useUnmanagedBusinessAutoRegisterConditionOptions({
  requiredConditionFieldTypes,
});

/**
 * 現在の設定一覧を返す
 */
const getCurrentSettings = (): UnmanagedBusinessAutoRegisterSettingItem[] => {
  return state.condition.settings;
};

const { loadOptionalItemOptionSources, getOptionalItemOptions } = useUnmanagedBusinessAutoRegisterOptionalItemOptions({
  getSettings: getCurrentSettings,
  getConditionValueOptions,
});

const newConditionOptionInput = reactive<NewConditionOptionInput>({
  targetFieldType: requiredConditionFieldTypes[0],
  code: '',
  name: '',
  value: '',
});

/**
 * 指定された条件項目の入力初期値を作成する
 * @param targetFieldType - 条件項目
 */
const createDefaultConditionItemByFieldType = (
  targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType
): UnmanagedBusinessAutoRegisterSettingConditionItem => ({
  targetFieldType,
  searchType: getConditionSearchTypesByFieldType(targetFieldType)[0],
  targetValue: '',
});

/**
 * 編集フォーム用に必須6KEY分の条件を正規化する
 * @param conditions - 元の条件一覧
 */
const normalizeConditionsForForm = (
  conditions: UnmanagedBusinessAutoRegisterSettingConditionItem[]
): UnmanagedBusinessAutoRegisterSettingConditionItem[] => {
  return requiredConditionFieldTypes.map((targetFieldType) => {
    const conditionItem = conditions.find((candidateConditionItem) => candidateConditionItem.targetFieldType === targetFieldType);
    if (conditionItem == null) {
      return createDefaultConditionItemByFieldType(targetFieldType);
    }

    const availableSearchTypes = getConditionSearchTypesByFieldType(targetFieldType);
    const normalizedSearchType = availableSearchTypes.includes(conditionItem.searchType)
      ? conditionItem.searchType
      : availableSearchTypes[0];
    return {
      targetFieldType,
      searchType: normalizedSearchType,
      targetValue: conditionItem.targetValue,
      targetName: conditionItem.targetName,
    };
  });
};

/**
 * 一覧行のアコーディオン開閉を判定する
 * @param settingId - 設定ID
 */
const isSettingOpened = (settingId: string): boolean => {
  return state.openedSettingIds.includes(settingId);
};

/**
 * 一覧行のアコーディオンを開閉する
 * @param settingId - 設定ID
 */
const toggleSetting = (settingId: string): void => {
  toggleOpenedSetting(settingId);
};

/**
 * UNIX時刻を画面表示用の日付文字列へ変換する
 * @param unixTime - UNIX時刻
 */
const formatDate = (unixTime: number): string => {
  const date = new Date(unixTime);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * 条件一覧表示用のコード/名称文字列を作成する
 * @param conditionItem - 条件
 */
const getConditionCodeNameDisplayText = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string => {
  const targetValue = conditionItem.targetValue.trim();
  if (targetValue === '') {
    return '-';
  }

  if (conditionItem.targetFieldType === 'package') {
    return targetValue;
  }

  const codeNamePair = getConditionCodeNamePair(conditionItem);
  if (codeNamePair != null) {
    return `${codeNamePair.code}（${t('名称')}：${codeNamePair.name}）`;
  }

  if (conditionItem.searchType === 'code') {
    return `${targetValue}（${t('名称')}：-）`;
  }
  return `-（${t('名称')}：${targetValue}）`;
};

/**
 * 条件一覧表示用の要約文字列を作成する
 * @param setting - 設定
 */
const getConditionSummary = (setting: UnmanagedBusinessAutoRegisterSettingItem): string => {
  const conditionSummaries = setting.conditions
    .map((conditionItem) => {
      const targetValue = conditionItem.targetValue.trim();
      if (targetValue === '') {
        return '';
      }
      return `${getConditionFieldTypeLabel(conditionItem.targetFieldType)}: ${getConditionCodeNameDisplayText(conditionItem)}`;
    })
    .filter((summary) => summary !== '');

  if (conditionSummaries.length === 0) {
    return t('条件未設定') as string;
  }

  return conditionSummaries.slice(0, 2).join(', ');
};

/**
 * 有効条件数を返す
 * @param setting - 設定
 */
const getEffectiveConditionCount = (setting: UnmanagedBusinessAutoRegisterSettingItem): number => {
  return setting.conditions.filter((conditionItem) => conditionItem.targetValue.trim() !== '').length;
};

/**
 * 条件詳細表示用の文字列を作成する
 * @param conditionItem - 条件
 */
const getConditionDetailText = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string => {
  const conditionFieldLabel = getConditionFieldTypeLabel(conditionItem.targetFieldType);
  const conditionDisplayValue = getConditionCodeNameDisplayText(conditionItem);
  if (conditionDisplayValue === '-') {
    return `${conditionFieldLabel}: -`;
  }

  return `${conditionFieldLabel}: ${conditionDisplayValue}`;
};

/**
 * 任意項目件数表示を返す
 * @param setting - 設定
 */
const getOptionalItemLabel = (setting: UnmanagedBusinessAutoRegisterSettingItem): string => {
  const validOptionalItemCount = setting.registerContent.optionalItems.filter(
    (optionalItem) => optionalItem.targetValue.trim() !== ''
  ).length;

  return validOptionalItemCount === 0 ? (t('なし') as string) : `${validOptionalItemCount}${t('件')}`;
};

/**
 * リードタイム表示文字列を作成する
 * @param setting - 設定
 */
const getLeadTimeLabel = (setting: UnmanagedBusinessAutoRegisterSettingItem): string => {
  return `${setting.registerContent.leadTime}${getRegisterUnitLabel(setting.registerContent.leadTimeUnit)}`;
};

/**
 * 在庫指数表示文字列を作成する
 * @param setting - 設定
 */
const getStdInventoryIndicesLabel = (setting: UnmanagedBusinessAutoRegisterSettingItem): string => {
  return `${setting.registerContent.stdInventoryIndicesMin} ～ ${setting.registerContent.stdInventoryIndicesMax}${getRegisterUnitLabel(
    setting.registerContent.stdInventoryIndicesUnit
  )}`;
};

/**
 * 反映後の設定一覧からアコーディオン開閉状態を解決する
 * @param settings - 反映後の設定一覧
 * @param previousOpenedSettingIds - 反映前の展開中設定ID一覧
 * @param openFirstIfNone - 展開対象がない場合に先頭を開くか
 */
const resolveOpenedSettingIds = (
  settings: UnmanagedBusinessAutoRegisterSettingItem[],
  previousOpenedSettingIds: string[] = [],
  openFirstIfNone = true
): string[] => {
  const existingSettingIds = new Set(settings.map((setting) => setting.settingId));
  const nextOpenedSettingIds = previousOpenedSettingIds.filter((openedSettingId) => existingSettingIds.has(openedSettingId));

  if (nextOpenedSettingIds.length > 0 || !openFirstIfNone) {
    return nextOpenedSettingIds;
  }

  if (settings.length === 0) {
    return [];
  }

  return [settings[0].settingId];
};

/**
 * サーバー取得値で画面状態を更新する
 * @param condition - 設定条件
 * @param updateAt - 更新時刻
 * @param options - 画面状態反映オプション
 */
const applyServerSetting = (
  condition: UnmanagedBusinessAutoRegisterCondition,
  updateAt: number | undefined,
  options: ApplyServerSettingOptions = {}
): void => {
  const { previousOpenedSettingIds = [], openFirstIfNone = true } = options;
  const normalizedCondition = normalizeUnmanagedBusinessAutoRegisterCondition(condition);
  normalizedCondition.settings = normalizedCondition.settings.map((setting) => ({
    ...cloneUnmanagedBusinessAutoRegisterSettingItem(setting),
    conditions: normalizeConditionsForForm(setting.conditions),
  }));
  normalizedCondition.enabled = normalizedCondition.settings.length > 0;
  appendConditionValueOptionsFromSettings(normalizedCondition.settings);
  normalizedCondition.settings.forEach((setting) => {
    setting.conditions.forEach((conditionItem) => {
      void syncConditionCounterpartOption(conditionItem);
    });
  });

  setCondition(normalizedCondition);
  setUpdateAt(updateAt);
  setOpenedSettingIds(resolveOpenedSettingIds(normalizedCondition.settings, previousOpenedSettingIds, openFirstIfNone));
};

/**
 * エラーモーダルを表示する
 * @param title - モーダルタイトル
 * @param description - モーダル本文
 */
const showErrorModal = async (title: string, description: string): Promise<void> => {
  await showInfoModal({
    modalTitle: title,
    modalDescription: description,
    backButtonLabel: t('OK'),
    isDisabledNextButton: true,
    enableOutsideClick: false,
    icon: 'error',
    colorStyle: 'error',
  });
};

/**
 * 設定をサーバーから取得する
 */
const loadSettingFromServer = async (): Promise<void> => {
  const result = await diContainer.getUnmanagedBusinessAutoRegisterSettingUseCase.Execute();

  if (result.status.type === 'success') {
    applyServerSetting(result.status.data.condition, result.status.data.updateAt, {
      previousOpenedSettingIds: state.openedSettingIds,
      openFirstIfNone: true,
    });
    return;
  }

  applyServerSetting(createDefaultUnmanagedBusinessAutoRegisterCondition(), undefined, {
    previousOpenedSettingIds: state.openedSettingIds,
    openFirstIfNone: true,
  });
  await showErrorModal(t('未管理取引の自動登録設定の取得に失敗しました'), t('通信環境を確認して再度お試しください。') as string);
};

/**
 * 編集フォームで使用する設定を初期化する
 * @param mode - 画面モード
 * @param setting - 編集対象の設定
 */
const openForm = (mode: Exclude<SettingPageMode, 'list'>, setting?: UnmanagedBusinessAutoRegisterSettingItem): void => {
  setMode(mode);
  resetValidationState();

  if (setting == null) {
    setEditingSettingId(undefined);
    const defaultFormSetting = createDefaultUnmanagedBusinessAutoRegisterSettingItem();
    setFormSetting(defaultFormSetting);
    setFormConditions(normalizeConditionsForForm([]));
    syncNumericInputValues();
    syncFormValidationState(false);
    return;
  }

  setEditingSettingId(setting.settingId);
  const editableFormSetting = cloneUnmanagedBusinessAutoRegisterSettingItem(setting);
  setFormSetting(editableFormSetting);
  setFormConditions(normalizeConditionsForForm(state.formSetting.conditions));
  syncNumericInputValues();
  syncFormValidationState(false);
  state.formSetting.conditions.forEach((conditionItem) => {
    void syncConditionCounterpartOption(conditionItem);
  });
};

/**
 * 一覧画面へ戻る
 */
const backToList = (): void => {
  setMode('list');
  setEditingSettingId(undefined);
  resetValidationState();
};

/**
 * 追加画面を開く
 */
const openCreateForm = (): void => {
  openForm('create');
};

/**
 * 編集画面を開く
 * @param setting - 編集対象
 */
const openEditForm = (setting: UnmanagedBusinessAutoRegisterSettingItem): void => {
  openForm('edit', setting);
};

/**
 * 検索方法を更新する
 * @param conditionIndex - 更新対象の条件index
 * @param selectedLabel - SelectInputで選択したラベル
 */
const updateConditionSearchType = (conditionIndex: number, selectedLabel: string): void | Promise<void> => {
  const conditionItem = state.formSetting.conditions[conditionIndex];
  const selectedSearchType = convertLabelToConditionSearchType(selectedLabel);
  const availableSearchTypes = getConditionSearchTypesByFieldType(conditionItem.targetFieldType);

  const normalizedSearchType = availableSearchTypes.includes(selectedSearchType) ? selectedSearchType : availableSearchTypes[0];
  setFormConditionSearchType(conditionIndex, normalizedSearchType);
  setFormConditionTargetValue(conditionIndex, '');
  revalidateConditionValuesIfNeeded();
  return syncConditionCounterpartOption(state.formSetting.conditions[conditionIndex]);
};

/**
 * 条件値を更新する
 * @param conditionIndex - 更新対象の条件index
 * @param selectedValue - 選択値
 */
const updateConditionValue = async (conditionIndex: number, selectedValue: string): Promise<void> => {
  const conditionItem = state.formSetting.conditions[conditionIndex];
  setFormConditionTargetValue(conditionIndex, selectedValue);
  appendConditionOptionValue(conditionItem.targetFieldType, conditionItem.searchType, selectedValue);
  revalidateConditionValuesIfNeeded();
  await syncConditionCounterpartOption(state.formSetting.conditions[conditionIndex]);
};

/**
 * 新規候補に条件項目を追加する
 */
const addNewConditionOption = async (): Promise<void> => {
  const conditionIndex = state.formSetting.conditions.findIndex(
    (conditionItem) => conditionItem.targetFieldType === newConditionOptionInput.targetFieldType
  );

  if (newConditionOptionInput.targetFieldType === 'package') {
    const normalizedValue = newConditionOptionInput.value.trim();
    if (normalizedValue === '') {
      await showErrorModal(t('入力内容を確認してください'), t('容量の値を入力してください') as string);
      return;
    }
    appendConditionOptionValue('package', 'value', normalizedValue);
    if (conditionIndex >= 0) {
      setFormConditionSearchType(conditionIndex, 'value');
      setFormConditionTargetValue(conditionIndex, normalizedValue);
      revalidateConditionValuesIfNeeded();
    }
    newConditionOptionInput.value = '';
    return;
  }

  const normalizedCode = newConditionOptionInput.code.trim();
  const normalizedName = newConditionOptionInput.name.trim();
  if (normalizedCode === '' || normalizedName === '') {
    await showErrorModal(t('入力内容を確認してください'), t('コードと名称の両方を入力してください') as string);
    return;
  }

  appendConditionOptionPair(newConditionOptionInput.targetFieldType, normalizedCode, normalizedName);
  if (conditionIndex >= 0) {
    setFormConditionSearchType(conditionIndex, 'code');
    setFormConditionTargetValue(conditionIndex, normalizedCode);
    revalidateConditionValuesIfNeeded();
  }
  newConditionOptionInput.code = '';
  newConditionOptionInput.name = '';
};

/**
 * 任意項目を追加する
 */
const addOptionalItem = (): void => {
  const usedFields = new Set(getUsedOptionalFields());
  const nextAvailableField = optionalRegisterFields.find((optionalField) => !usedFields.has(optionalField));
  if (nextAvailableField == null) {
    return;
  }

  const newOptionalItem = createDefaultUnmanagedBusinessAutoRegisterOptionalRegisterItem();
  newOptionalItem.targetField = nextAvailableField;
  addFormOptionalItem(newOptionalItem);
  revalidateOptionalItemsIfNeeded();
};

/**
 * 任意項目を削除する
 * @param optionalItemIndex - 削除対象index
 */
const removeOptionalItem = (optionalItemIndex: number): void => {
  removeFormOptionalItem(optionalItemIndex);
  revalidateOptionalItemsIfNeeded();
};

/**
 * 任意項目の対象フィールドを更新する
 * @param optionalItemIndex - 任意項目index
 * @param selectedLabel - SelectInputで選択したラベル
 */
const updateOptionalItemField = (optionalItemIndex: number, selectedLabel: string): void => {
  const selectedTargetField = convertLabelToTargetField(selectedLabel);
  const isSelectedByOtherOptionalItem = state.formSetting.registerContent.optionalItems.some((optionalItem, index) => {
    return index !== optionalItemIndex && optionalItem.targetField === selectedTargetField;
  });
  if (isSelectedByOtherOptionalItem) {
    return;
  }

  setFormOptionalItemTargetField(optionalItemIndex, selectedTargetField);
  revalidateOptionalItemsIfNeeded();
};

/**
 * 入力値を保存形式へ整形する
 */
const normalizeFormSetting = async (): Promise<{
  setting: UnmanagedBusinessAutoRegisterSettingItem;
  unresolvedConditionFieldLabels: string[];
}> => {
  const unresolvedConditionFieldLabelSet = new Set<string>();
  const normalizedConditions: UnmanagedBusinessAutoRegisterSettingConditionItem[] = [];

  for (const conditionItem of state.formSetting.conditions) {
    const normalizedTargetValue = conditionItem.targetValue.trim();
    if (normalizedTargetValue === '') {
      continue;
    }

    if (conditionItem.targetFieldType === 'package') {
      normalizedConditions.push({
        targetFieldType: conditionItem.targetFieldType,
        searchType: conditionItem.searchType,
        targetValue: normalizedTargetValue,
      });
      continue;
    }

    const normalizedConditionPair = await resolveConditionCodeNamePair({
      ...conditionItem,
      targetValue: normalizedTargetValue,
    });
    if (normalizedConditionPair == null) {
      unresolvedConditionFieldLabelSet.add(getConditionFieldTypeLabel(conditionItem.targetFieldType));
      continue;
    }

    normalizedConditions.push({
      targetFieldType: conditionItem.targetFieldType,
      searchType: 'code',
      targetValue: normalizedConditionPair.code,
      targetName: normalizedConditionPair.name,
    });
  }

  return {
    setting: {
      ...cloneUnmanagedBusinessAutoRegisterSettingItem(state.formSetting),
      settingName: state.formSetting.settingName.trim(),
      conditions: normalizedConditions,
      registerContent: {
        ...state.formSetting.registerContent,
        leadTime: Number(numericInputValues.leadTime),
        stdInventoryIndicesMin: Number(numericInputValues.stdInventoryIndicesMin),
        stdInventoryIndicesMax: Number(numericInputValues.stdInventoryIndicesMax),
        optionalItems: state.formSetting.registerContent.optionalItems
          .map((optionalItem) => ({
            targetField: optionalItem.targetField,
            targetValue: numericOptionalRegisterFields.has(optionalItem.targetField)
              ? String(Number(optionalItem.targetValue.trim()))
              : optionalItem.targetValue.trim(),
          }))
          .filter((optionalItem) => optionalItem.targetValue !== ''),
      },
    },
    unresolvedConditionFieldLabels: [...unresolvedConditionFieldLabelSet],
  };
};

/**
 * 設定一覧をサーバーへ保存する
 * @param settings - 保存対象の設定一覧
 */
const saveSettings = async (settings: UnmanagedBusinessAutoRegisterSettingItem[]): Promise<boolean> => {
  if (state.isSaving) {
    return false;
  }

  const previousOpenedSettingIds = [...state.openedSettingIds];

  setIsSaving(true);

  const normalizedSettings = settings.map((setting) => ({
    ...cloneUnmanagedBusinessAutoRegisterSettingItem(setting),
    settingName: setting.settingName.trim(),
  }));

  const saveCondition: UnmanagedBusinessAutoRegisterCondition = {
    ...cloneUnmanagedBusinessAutoRegisterCondition(state.condition),
    enabled: normalizedSettings.length > 0,
    settings: normalizedSettings,
  };

  const result = await diContainer.saveUnmanagedBusinessAutoRegisterSettingUseCase.Execute({
    condition: saveCondition,
    updateAt: state.updateAt,
  });

  setIsSaving(false);

  if (result.status.type === 'success') {
    applyServerSetting(result.status.data.condition, result.status.data.updateAt, {
      previousOpenedSettingIds,
      openFirstIfNone: false,
    });
    return true;
  }

  if (result.status.error.code === 'UPDATE_TIMING_CONFLICT') {
    await showErrorModal(
      t('未管理取引の自動登録設定の保存に失敗しました'),
      t('他のユーザーの更新と競合しました。最新の設定を再読み込みしたので、内容を確認して再度変更してください。') as string
    );
    await loadSettingFromServer();
    return false;
  }

  await showErrorModal(t('未管理取引の自動登録設定の保存に失敗しました'), t('通信環境を確認して再度お試しください。') as string);

  return false;
};

/**
 * 追加/編集フォームを保存する
 */
const saveFormSetting = async (): Promise<void> => {
  const validateError = validateForm();
  if (validateError != null) {
    await showErrorModal(t('入力内容を確認してください'), validateError);
    return;
  }

  const normalizedResult = await normalizeFormSetting();
  if (normalizedResult.unresolvedConditionFieldLabels.length > 0) {
    await showErrorModal(
      t('入力内容を確認してください'),
      `${t('以下の条件項目でコードを特定できませんでした') as string}：${normalizedResult.unresolvedConditionFieldLabels.join('、')}`
    );
    return;
  }

  const nextSettings =
    state.mode === 'edit'
      ? state.condition.settings.map((setting) => (setting.settingId === state.editingSettingId ? normalizedResult.setting : setting))
      : [...state.condition.settings, normalizedResult.setting];

  const saved = await saveSettings(nextSettings);
  if (saved) {
    backToList();
  }
};

/**
 * 設定を複製する
 * @param setting - 複製元設定
 */
const duplicateSetting = async (setting: UnmanagedBusinessAutoRegisterSettingItem): Promise<void> => {
  const copiedSetting = cloneUnmanagedBusinessAutoRegisterSettingItem(setting);
  copiedSetting.settingId = createUnmanagedBusinessAutoRegisterSettingId();
  copiedSetting.settingName =
    copiedSetting.settingName.trim() === '' ? (t('コピー設定') as string) : `${copiedSetting.settingName}${t('（コピー）')}`;
  copiedSetting.createdAt = Date.now();

  await saveSettings([...state.condition.settings, copiedSetting]);
};

/**
 * 設定を削除する
 * @param setting - 削除対象
 */
const deleteSetting = async (setting: UnmanagedBusinessAutoRegisterSettingItem): Promise<void> => {
  const displaySettingName = setting.settingName.trim() === '' ? (t('無題') as string) : setting.settingName;

  const confirmed = await showInfoModal({
    modalTitle: t('設定を削除しますか'),
    modalDescription: `${t('設定名') as string}：${displaySettingName}`,
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('削除する'),
    enableOutsideClick: false,
    icon: 'error',
    colorStyle: 'error',
  });

  if (!confirmed) {
    return;
  }

  const nextSettings = state.condition.settings.filter((targetSetting) => targetSetting.settingId !== setting.settingId);
  await saveSettings(nextSettings);
};

/**
 * フォームで使用するSelectInput indexを返す
 * @param base - ベース値
 * @param offset - オフセット
 */
const getFormSelectInputIndex = (base: number, offset: number): number => {
  return base * 100 + offset;
};

onBeforeMount(async () => {
  resetState();
  resetConditionValueOptions();
  await Promise.all([loadOptionalItemOptionSources(), loadConditionValueOptionsFromApi()]);
  await loadSettingFromServer();
  setInitialized(true);
});
</script>

<template>
  <section class="setting-unmanaged-business-page">
    <header class="setting-unmanaged-business-page__header">
      <SettingsPageTitle :label="t('設定 > 未管理取引')" />
    </header>

    <div v-if="state.mode === 'list'" class="setting-unmanaged-business-page__container">
      <div class="setting-unmanaged-business-page__top-area">
        <div class="setting-unmanaged-business-page__title-block">
          <h2 class="setting-unmanaged-business-page__title">
            {{ t('自動登録条件設定') }}
          </h2>
          <p class="setting-unmanaged-business-page__subtitle">
            {{ t('条件に応じた自動登録設定を管理します') }}
            {{ `（${t('設定数')} ${state.condition.settings.length}）` }}
          </p>
        </div>
        <button type="button" class="setting-unmanaged-business-page__primary-button" :disabled="state.isSaving" @click="openCreateForm">
          {{ t('新規設定を追加') }}
        </button>
      </div>

      <div class="setting-unmanaged-business-page__table-wrapper">
        <div class="setting-unmanaged-business-page__table-head-row">
          <p />
          <p>{{ t('設定名') }}</p>
          <p>{{ t('条件') }}</p>
          <p>{{ t('リードタイム') }}</p>
          <p>{{ t('在庫指数') }}</p>
          <p>{{ t('任意項目') }}</p>
          <p>{{ t('操作') }}</p>
        </div>

        <div v-if="state.condition.settings.length === 0" class="setting-unmanaged-business-page__empty-row">
          {{ t('設定がありません。新規設定を追加してください。') }}
        </div>

        <div
          v-for="(setting, settingIndex) in state.condition.settings"
          :key="setting.settingId"
          class="setting-unmanaged-business-page__table-body"
        >
          <div class="setting-unmanaged-business-page__table-row">
            <button
              type="button"
              class="setting-unmanaged-business-page__icon-button setting-unmanaged-business-page__expand-button"
              @click="toggleSetting(setting.settingId)"
            >
              <Mdi :icon="isSettingOpened(setting.settingId) ? 'expand_less' : 'expand_more'" aria-label="toggle" />
            </button>

            <div class="setting-unmanaged-business-page__name-cell">
              <p class="setting-unmanaged-business-page__name-text">
                {{ setting.settingName.trim() === '' ? t('無題') : setting.settingName }}
              </p>
              <p class="setting-unmanaged-business-page__date-text">
                {{ formatDate(setting.createdAt) }}
              </p>
            </div>

            <div class="setting-unmanaged-business-page__condition-cell">
              <span class="setting-unmanaged-business-page__count-chip">
                {{ `${getEffectiveConditionCount(setting)}${t('件')}` }}
              </span>
              <p class="setting-unmanaged-business-page__condition-summary">
                {{ getConditionSummary(setting) }}
              </p>
            </div>

            <p class="setting-unmanaged-business-page__text-cell">
              {{ getLeadTimeLabel(setting) }}
            </p>

            <p class="setting-unmanaged-business-page__text-cell">
              {{ getStdInventoryIndicesLabel(setting) }}
            </p>

            <p class="setting-unmanaged-business-page__text-cell">
              {{ getOptionalItemLabel(setting) }}
            </p>

            <div class="setting-unmanaged-business-page__action-cell">
              <button
                type="button"
                class="setting-unmanaged-business-page__icon-button"
                :disabled="state.isSaving"
                @click="openEditForm(setting)"
              >
                <Mdi icon="edit" :aria-label="t('編集') as string" />
              </button>
              <button
                type="button"
                class="setting-unmanaged-business-page__icon-button"
                :disabled="state.isSaving"
                @click="duplicateSetting(setting)"
              >
                <Mdi icon="content_copy" :aria-label="t('複製') as string" />
              </button>
              <button
                type="button"
                class="setting-unmanaged-business-page__icon-button"
                :disabled="state.isSaving"
                @click="deleteSetting(setting)"
              >
                <Mdi icon="delete_outline" :aria-label="t('削除') as string" />
              </button>
            </div>
          </div>

          <div v-if="isSettingOpened(setting.settingId)" class="setting-unmanaged-business-page__detail-row">
            <div class="setting-unmanaged-business-page__detail-block">
              <p class="setting-unmanaged-business-page__detail-title">{{ t('条件詳細') }}</p>
              <p
                v-for="(conditionItem, conditionIndex) in setting.conditions"
                :key="`${setting.settingId}-condition-${conditionIndex}`"
                class="setting-unmanaged-business-page__detail-text"
              >
                {{ getConditionDetailText(conditionItem) }}
              </p>
            </div>

            <div class="setting-unmanaged-business-page__detail-block">
              <p class="setting-unmanaged-business-page__detail-title">{{ t('登録項目詳細') }}</p>
              <p class="setting-unmanaged-business-page__detail-text">
                {{
                  `${t('リードタイム')}：${setting.registerContent.leadTime}${getRegisterUnitLabel(setting.registerContent.leadTimeUnit)}`
                }}
              </p>
              <p class="setting-unmanaged-business-page__detail-text">
                {{
                  `${t('標準在庫指数')}：${setting.registerContent.stdInventoryIndicesMin} ～ ${
                    setting.registerContent.stdInventoryIndicesMax
                  }${getRegisterUnitLabel(setting.registerContent.stdInventoryIndicesUnit)}`
                }}
              </p>
              <template v-if="setting.registerContent.optionalItems.length > 0">
                <p class="setting-unmanaged-business-page__detail-text">{{ t('任意項目') }}：</p>
                <p
                  v-for="(optionalItem, optionalItemIndex) in setting.registerContent.optionalItems"
                  :key="`${setting.settingId}-optional-${optionalItemIndex}`"
                  class="setting-unmanaged-business-page__detail-text"
                >
                  {{ `${getFieldLabel(optionalItem.targetField)}：${optionalItem.targetValue}` }}
                </p>
              </template>
              <p v-else class="setting-unmanaged-business-page__detail-text">
                {{ `${t('任意項目')}：${t('なし')}` }}
              </p>
            </div>
          </div>

          <div v-if="settingIndex !== state.condition.settings.length - 1" class="setting-unmanaged-business-page__row-border" />
        </div>
      </div>

      <p class="setting-unmanaged-business-page__note">
        {{ t('1設定内はAND条件、設定同士はOR条件で判定される。') }}
      </p>
    </div>

    <div v-else class="setting-unmanaged-business-page__container">
      <button type="button" class="setting-unmanaged-business-page__back-button" :disabled="state.isSaving" @click="backToList">
        <Mdi icon="arrow_back" />
        {{ t('一覧に戻る') }}
      </button>

      <div class="setting-unmanaged-business-page__editor-header">
        <h2 class="setting-unmanaged-business-page__title">
          {{ state.mode === 'create' ? t('新規設定を追加') : t('設定を編集') }}
        </h2>
        <p class="setting-unmanaged-business-page__subtitle">
          {{ state.mode === 'create' ? t('新しい自動登録条件設定を作成します') : t('自動登録条件設定を編集します') }}
        </p>
      </div>

      <section class="setting-unmanaged-business-page__form-card">
        <label class="setting-unmanaged-business-page__input-label">{{ t('設定名') }} *</label>
        <input
          :value="state.formSetting.settingName"
          class="setting-unmanaged-business-page__text-input"
          type="text"
          :placeholder="t('例: 東京倉庫 - 製品A設定')"
          :disabled="state.isSaving"
          @input="onSettingNameInput"
        />
        <ErrorMessage :is-error="fieldState.settingName.isError.value" :error-message="fieldState.settingName.errorMessage.value" />
      </section>

      <section class="setting-unmanaged-business-page__form-card">
        <div class="setting-unmanaged-business-page__section-header">
          <p class="setting-unmanaged-business-page__section-title">{{ t('条件設定') }}</p>
        </div>
        <p class="setting-unmanaged-business-page__section-sub-title">
          {{ t('KEY6項目（入力した項目のみAND判定）') }}
        </p>

        <div class="setting-unmanaged-business-page__conditions-card">
          <div
            v-for="(conditionItem, conditionIndex) in state.formSetting.conditions"
            :key="`form-condition-${conditionIndex}`"
            class="setting-unmanaged-business-page__condition-row"
          >
            <div class="setting-unmanaged-business-page__condition-number">
              {{ conditionIndex + 1 }}
            </div>

            <div class="setting-unmanaged-business-page__condition-fixed-field">
              <label class="setting-unmanaged-business-page__input-label">
                {{ t('条件項目') }}
              </label>
              <p class="setting-unmanaged-business-page__condition-fixed-value">
                {{ getConditionFieldTypeLabel(conditionItem.targetFieldType) }}
              </p>
            </div>

            <SelectInput
              :index="getFormSelectInputIndex(conditionIndex, 1)"
              :model-value="getConditionSearchTypeSelectLabel(conditionItem)"
              :options="getSearchTypeLabelsByConditionItem(conditionItem)"
              :placeholder="t('選択してください')"
              :label="t('検索方法')"
              :show-deselect-option="false"
              :disabled="state.isSaving"
              @update:model-value="(selectedLabel) => updateConditionSearchType(conditionIndex, selectedLabel)"
            />

            <SelectInput
              :index="getFormSelectInputIndex(conditionIndex, 2)"
              :model-value="conditionItem.targetValue"
              :options="getConditionValueOptions(conditionItem)"
              :placeholder="t('選択してください')"
              :label="t('値')"
              :show-deselect-option="false"
              :disabled="state.isSaving"
              @update:model-value="(selectedValue) => updateConditionValue(conditionIndex, selectedValue)"
            />

            <p class="setting-unmanaged-business-page__condition-counterpart">
              {{ getConditionCounterpartText(conditionItem) }}
            </p>
          </div>
        </div>
        <ErrorMessage :is-error="fieldState.conditionValues.isError.value" :error-message="fieldState.conditionValues.errorMessage.value" />

        <div class="setting-unmanaged-business-page__new-option-card">
          <p class="setting-unmanaged-business-page__section-sub-title">
            {{ t('候補にない値を追加') }}
          </p>
          <div class="setting-unmanaged-business-page__new-option-grid">
            <SelectInput
              :index="3101"
              :model-value="getConditionFieldTypeLabel(newConditionOptionInput.targetFieldType)"
              :options="getConditionFieldTypeOptions()"
              :placeholder="t('選択してください')"
              :label="t('条件項目')"
              :show-deselect-option="false"
              :disabled="state.isSaving"
              @update:model-value="
                (selectedLabel) => (newConditionOptionInput.targetFieldType = convertLabelToConditionFieldType(selectedLabel))
              "
            />

            <template v-if="newConditionOptionInput.targetFieldType === 'package'">
              <div class="setting-unmanaged-business-page__register-item">
                <label class="setting-unmanaged-business-page__input-label">{{ t('容量') }}</label>
                <input
                  v-model="newConditionOptionInput.value"
                  class="setting-unmanaged-business-page__text-input"
                  type="text"
                  :placeholder="t('値を入力')"
                  :disabled="state.isSaving"
                />
              </div>
            </template>
            <template v-else>
              <div class="setting-unmanaged-business-page__register-item">
                <label class="setting-unmanaged-business-page__input-label">{{ t('コード') }}</label>
                <input
                  v-model="newConditionOptionInput.code"
                  class="setting-unmanaged-business-page__text-input"
                  type="text"
                  :placeholder="t('値を入力')"
                  :disabled="state.isSaving"
                />
              </div>
              <div class="setting-unmanaged-business-page__register-item">
                <label class="setting-unmanaged-business-page__input-label">{{ t('名称') }}</label>
                <input
                  v-model="newConditionOptionInput.name"
                  class="setting-unmanaged-business-page__text-input"
                  type="text"
                  :placeholder="t('値を入力')"
                  :disabled="state.isSaving"
                />
              </div>
            </template>

            <button
              type="button"
              class="setting-unmanaged-business-page__secondary-button"
              :disabled="state.isSaving"
              @click="addNewConditionOption"
            >
              <Mdi icon="add" />
              {{ t('候補に追加') }}
            </button>
          </div>
        </div>
      </section>

      <section class="setting-unmanaged-business-page__form-card">
        <p class="setting-unmanaged-business-page__section-title">
          {{ t('条件に合致した場合の登録項目') }}
        </p>

        <p class="setting-unmanaged-business-page__section-sub-title">
          {{ t('必須項目') }}
        </p>

        <div class="setting-unmanaged-business-page__register-grid">
          <div class="setting-unmanaged-business-page__register-item">
            <label class="setting-unmanaged-business-page__input-label">{{ t('リードタイム') }} *</label>
            <input
              :value="numericInputValues.leadTime"
              class="setting-unmanaged-business-page__text-input"
              type="number"
              min="1"
              step="1"
              :placeholder="t('数値を入力')"
              :disabled="state.isSaving"
              @input="onLeadTimeInput"
            />
            <ErrorMessage :is-error="fieldState.leadTime.isError.value" :error-message="fieldState.leadTime.errorMessage.value" />
          </div>

          <SelectInput
            :index="2001"
            :model-value="getRegisterUnitLabel(state.formSetting.registerContent.leadTimeUnit)"
            :options="getRegisterUnitOptions()"
            :placeholder="t('選択してください')"
            :label="`${t('リードタイム単位')} *`"
            :show-deselect-option="false"
            :disabled="state.isSaving"
            @update:model-value="(selectedLabel) => setFormLeadTimeUnit(convertLabelToRegisterUnit(selectedLabel))"
          />

          <div class="setting-unmanaged-business-page__register-item">
            <label class="setting-unmanaged-business-page__input-label">{{ t('標準在庫指数min') }} *</label>
            <input
              :value="numericInputValues.stdInventoryIndicesMin"
              class="setting-unmanaged-business-page__text-input"
              type="number"
              min="0.01"
              step="0.01"
              :placeholder="t('数値を入力')"
              :disabled="state.isSaving"
              @input="onStdInventoryIndicesMinInput"
            />
            <ErrorMessage
              :is-error="fieldState.stdInventoryIndicesMin.isError.value"
              :error-message="fieldState.stdInventoryIndicesMin.errorMessage.value"
            />
          </div>

          <div class="setting-unmanaged-business-page__register-item">
            <label class="setting-unmanaged-business-page__input-label">{{ t('標準在庫指数max') }} *</label>
            <input
              :value="numericInputValues.stdInventoryIndicesMax"
              class="setting-unmanaged-business-page__text-input"
              type="number"
              min="0.01"
              step="0.01"
              :placeholder="t('数値を入力')"
              :disabled="state.isSaving"
              @input="onStdInventoryIndicesMaxInput"
            />
            <ErrorMessage
              :is-error="fieldState.stdInventoryIndicesMax.isError.value"
              :error-message="fieldState.stdInventoryIndicesMax.errorMessage.value"
            />
          </div>

          <SelectInput
            :index="2002"
            :model-value="getRegisterUnitLabel(state.formSetting.registerContent.stdInventoryIndicesUnit)"
            :options="getRegisterUnitOptions()"
            :placeholder="t('選択してください')"
            :label="`${t('標準在庫指数単位')} *`"
            :show-deselect-option="false"
            :disabled="state.isSaving"
            @update:model-value="(selectedLabel) => setFormStdInventoryIndicesUnit(convertLabelToRegisterUnit(selectedLabel))"
          />
        </div>

        <div class="setting-unmanaged-business-page__section-header setting-unmanaged-business-page__optional-header">
          <p class="setting-unmanaged-business-page__section-sub-title">
            {{ t('任意項目') }}
          </p>
          <button
            type="button"
            class="setting-unmanaged-business-page__subtle-button"
            :disabled="state.isSaving || !canAddOptionalItem()"
            @click="addOptionalItem"
          >
            <Mdi icon="add" />
            {{ t('項目を追加') }}
          </button>
        </div>

        <div v-if="state.formSetting.registerContent.optionalItems.length === 0" class="setting-unmanaged-business-page__optional-empty">
          {{ t('任意項目を追加できます') }}
        </div>

        <div
          v-for="(optionalItem, optionalItemIndex) in state.formSetting.registerContent.optionalItems"
          :key="`optional-item-${optionalItemIndex}`"
          class="setting-unmanaged-business-page__optional-row"
        >
          <SelectInput
            :index="getFormSelectInputIndex(130 + optionalItemIndex, 1)"
            :model-value="getFieldLabel(optionalItem.targetField)"
            :options="getOptionalFieldSelectOptions(optionalItemIndex)"
            :placeholder="t('選択してください')"
            :label="t('項目')"
            :show-deselect-option="false"
            :disabled="state.isSaving"
            @update:model-value="(selectedLabel) => updateOptionalItemField(optionalItemIndex, selectedLabel)"
          />

          <div class="setting-unmanaged-business-page__register-item">
            <label class="setting-unmanaged-business-page__input-label">{{ t('値') }}</label>
            <SuggestInput
              :model-value="optionalItem.targetValue"
              :options="getOptionalItemOptions(optionalItem.targetField, optionalItem.targetValue)"
              :placeholder="t('値を入力')"
              :disabled="state.isSaving"
              @update:model-value="(targetValue) => onOptionalItemTargetValueUpdate(optionalItemIndex, targetValue)"
            />
          </div>

          <button
            type="button"
            class="setting-unmanaged-business-page__danger-button"
            :disabled="state.isSaving"
            @click="removeOptionalItem(optionalItemIndex)"
          >
            <Mdi icon="delete_outline" />
            {{ t('削除') }}
          </button>
        </div>
        <ErrorMessage :is-error="fieldState.optionalItems.isError.value" :error-message="fieldState.optionalItems.errorMessage.value" />
      </section>

      <div class="setting-unmanaged-business-page__footer-actions">
        <button type="button" class="setting-unmanaged-business-page__cancel-button" :disabled="state.isSaving" @click="backToList">
          {{ t('キャンセル') }}
        </button>
        <button type="button" class="setting-unmanaged-business-page__primary-button" :disabled="state.isSaving" @click="saveFormSetting">
          {{ state.mode === 'create' ? t('追加') : t('保存') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.setting-unmanaged-business-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
  }

  &__container {
    display: grid;
    gap: 16px;
    background-color: $semanticWhite;
    border-radius: 8px;
    padding: 24px;
  }

  &__top-area {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
    gap: 16px;
  }

  &__title-block {
    display: grid;
    gap: 8px;
  }

  &__title {
    margin: 0;
    font-size: 32px;
    font-weight: 700;
    color: $textDefault;
  }

  &__subtitle {
    margin: 0;
    font-size: 16px;
    color: $textSecondary;
  }

  &__primary-button,
  &__secondary-button,
  &__cancel-button,
  &__danger-button,
  &__subtle-button,
  &__back-button,
  &__icon-button {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 8px;
    cursor: pointer;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 13px;
    line-height: 1;
  }

  &__primary-button {
    height: 44px;
    padding: 0 18px;
    color: $semanticWhite;
    background-color: $semanticPrimary;
  }

  &__secondary-button {
    height: 36px;
    padding: 0 14px;
    color: $semanticWhite;
    background-color: $semanticPrimary;
  }

  &__subtle-button {
    height: 36px;
    padding: 0 14px;
    color: $semanticWhite;
    background-color: $surfaceDisable;
  }

  &__cancel-button {
    height: 40px;
    min-width: 110px;
    padding: 0 16px;
    color: $textSecondary;
    background-color: $semanticWhite;
    border-color: $borderSecondary;
  }

  &__danger-button {
    height: 36px;
    padding: 0 12px;
    color: $semanticError;
    background-color: $semanticErrorSubdued;
    border-color: $semanticError;
  }

  &__back-button {
    justify-content: start;
    width: fit-content;
    height: 32px;
    padding: 0 8px;
    background-color: transparent;
    border: none;
    color: $textSecondary;
  }

  &__table-wrapper {
    border: 1px solid $borderDefault;
    border-radius: 12px;
    overflow: hidden;
    background-color: $semanticWhite;
  }

  &__table-head-row,
  &__table-row {
    display: grid;
    grid-template-columns: 40px minmax(220px, 2fr) minmax(250px, 2.3fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(
        130px,
        1fr
      );
    align-items: center;
    column-gap: 12px;
    padding: 0 16px;
  }

  &__table-head-row {
    height: 48px;
    background-color: $backgroundDefault;

    p {
      margin: 0;
      font-size: 12px;
      color: $textSecondary;
    }
  }

  &__table-body {
    display: grid;
  }

  &__table-row {
    min-height: 74px;
  }

  &__empty-row {
    min-height: 120px;
    display: grid;
    place-items: center;
    color: $textSecondary;
    font-size: 13px;
  }

  &__icon-button {
    width: 32px;
    height: 32px;
    padding: 0;
    background-color: transparent;
    border: none;
    color: $textSecondary;
  }

  &__expand-button {
    width: 24px;
    height: 24px;
  }

  &__name-cell,
  &__condition-cell,
  &__action-cell {
    display: grid;
    align-items: center;
    gap: 4px;
  }

  &__name-text,
  &__date-text,
  &__condition-summary,
  &__text-cell {
    margin: 0;
  }

  &__name-text {
    color: $textDefault;
    font-size: 14px;
    font-weight: 700;
  }

  &__date-text {
    color: $textSecondary;
    font-size: 12px;
  }

  &__condition-cell {
    grid-template-columns: auto 1fr;
    column-gap: 8px;
  }

  &__count-chip {
    display: grid;
    place-items: center;
    min-width: 36px;
    padding: 4px 6px;
    border-radius: 6px;
    color: $semanticPrimary;
    background-color: $semanticPrimarySubdued;
    font-size: 12px;
  }

  &__condition-summary,
  &__text-cell {
    color: $textDefault;
    font-size: 14px;
  }

  &__action-cell {
    grid-template-columns: repeat(3, 32px);
    justify-content: start;
  }

  &__detail-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    padding: 18px 24px;
    background-color: $backgroundDefault;
  }

  &__detail-block {
    display: grid;
    gap: 10px;
  }

  &__detail-title {
    margin: 0;
    font-size: 14px;
    color: $textDefault;
    font-weight: 700;
  }

  &__detail-text {
    margin: 0;
    font-size: 13px;
    color: $textSecondary;
    line-height: 1.5;
  }

  &__row-border {
    border-bottom: 1px solid $borderDefault;
  }

  &__note {
    margin: 0;
    color: $textSecondary;
    font-size: 12px;
  }

  &__editor-header {
    display: grid;
    gap: 8px;
  }

  &__form-card {
    display: grid;
    gap: 12px;
    border: 1px solid $borderDefault;
    border-radius: 12px;
    padding: 20px;
    background-color: $semanticWhite;
  }

  &__section-header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 8px;
  }

  &__section-title {
    margin: 0;
    font-size: 16px;
    color: $textDefault;
    font-weight: 700;
  }

  &__section-sub-title {
    margin: 0;
    font-size: 14px;
    color: $textDefault;
    font-weight: 700;
  }

  &__conditions-card {
    display: grid;
    gap: 12px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    padding: 16px;
  }

  &__condition-row {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    align-items: end;
    gap: 10px;
  }

  &__condition-fixed-field {
    display: grid;
    gap: 6px;
  }

  &__condition-fixed-value {
    margin: 0;
    height: 40px;
    display: grid;
    align-items: center;
    padding: 0 12px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    color: $textDefault;
    background-color: $semanticWhite;
    box-sizing: border-box;
  }

  &__condition-counterpart {
    margin: 0;
    grid-column: 2 / -1;
    min-height: 18px;
    color: $textSecondary;
    font-size: 12px;
  }

  &__condition-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: $semanticPrimary;
    background-color: $semanticPrimarySubdued;
    font-size: 13px;
    font-weight: 700;
  }

  &__condition-value-field,
  &__register-item {
    display: grid;
    gap: 6px;
  }

  &__input-label {
    color: $textSecondary;
    font-size: 13px;
  }

  &__text-input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    color: $textDefault;
    background-color: $semanticWhite;
    box-sizing: border-box;
  }

  &__register-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  &__optional-header {
    margin-top: 8px;
  }

  &__new-option-card {
    display: grid;
    gap: 12px;
    border: 1px dashed $borderDefault;
    border-radius: 8px;
    padding: 12px;
  }

  &__new-option-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    align-items: end;
  }

  &__optional-empty {
    min-height: 96px;
    border: 1px dashed $borderDefault;
    border-radius: 8px;
    display: grid;
    place-items: center;
    color: $textDisable;
    font-size: 14px;
  }

  &__optional-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    align-items: end;
    gap: 10px;
    padding: 10px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    background-color: $backgroundDefault;
  }

  &__footer-actions {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: end;
    gap: 12px;
    border: 1px solid $borderDefault;
    border-radius: 12px;
    padding: 16px;
  }

  &__primary-button:disabled,
  &__secondary-button:disabled,
  &__cancel-button:disabled,
  &__danger-button:disabled,
  &__subtle-button:disabled,
  &__back-button:disabled,
  &__icon-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

@media (max-width: 1200px) {
  .setting-unmanaged-business-page {
    &__table-head-row,
    &__table-row {
      grid-template-columns: 32px minmax(180px, 1.8fr) minmax(180px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(
          120px,
          1fr
        );
      column-gap: 8px;
      padding: 0 10px;
    }

    &__condition-row {
      grid-template-columns: 32px 1fr;
      gap: 10px;
    }

    &__condition-row > :nth-child(n + 2) {
      grid-column: 2;
    }

    &__new-option-grid {
      grid-template-columns: 1fr;
    }

    &__register-grid {
      grid-template-columns: 1fr;
    }

    &__optional-row {
      grid-template-columns: 1fr;
    }

    &__optional-row > :nth-child(3) {
      justify-self: start;
    }
  }
}

@media (max-width: 900px) {
  .setting-unmanaged-business-page {
    &__top-area {
      grid-template-columns: 1fr;
    }

    &__table-head-row {
      display: none;
    }

    &__table-wrapper {
      border: none;
      border-radius: 0;
      background-color: transparent;
      display: grid;
      gap: 12px;
    }

    &__table-body {
      border: 1px solid $borderDefault;
      border-radius: 10px;
      overflow: hidden;
      background-color: $semanticWhite;
    }

    &__table-row {
      grid-template-columns: 24px 1fr;
      row-gap: 8px;
      align-items: start;
      padding: 14px;
    }

    &__table-row > :nth-child(3),
    &__table-row > :nth-child(4),
    &__table-row > :nth-child(5),
    &__table-row > :nth-child(6),
    &__table-row > :nth-child(7) {
      grid-column: 2;
    }

    &__action-cell {
      justify-content: start;
    }

    &__detail-row {
      grid-template-columns: 1fr;
      padding: 14px;
    }

    &__footer-actions {
      grid-template-columns: 1fr;
    }

    &__cancel-button,
    &__primary-button {
      width: 100%;
    }
  }
}
</style>
